import { API_HOST } from './api';

export interface AuthUser {
  id: number;
  openId?: string;
  username?: string;
  name: string;
  email?: string;
  avatar?: string;
  loginType?: string;
  menuKeys?: string[];
}

export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
}

/**
 * 获取 Lark 授权 URL
 */
export async function getLarkAuthUrl(state?: string): Promise<string> {
  const params = state ? `?state=${encodeURIComponent(state)}` : '';
  const response = await fetch(`${API_HOST}/auth/lark/url${params}`);
  const data = await response.json();
  if (data.code !== 0) {
    throw new Error(data.msg || '获取授权地址失败');
  }
  return data.data.url;
}

/**
 * 处理 Lark OAuth 回调
 */
export async function handleLarkCallback(code: string, state?: string): Promise<{
  token: string;
  user: AuthUser;
}> {
  const params = new URLSearchParams({ code });
  if (state) params.append('state', state);

  const response = await fetch(`${API_HOST}/auth/lark/callback?${params}`);
  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.msg || '登录失败');
  }

  return {
    token: data.data.token,
    user: data.data.user
  };
}

/**
 * 账号密码登录
 */
export async function loginWithPassword(username: string, password: string): Promise<{
  token: string;
  user: AuthUser;
}> {
  const response = await fetch(`${API_HOST}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    // Try to parse error body, fallback to status text
    try {
      const data = await response.json();
      throw new Error(data.msg || `请求失败 (${response.status})`);
    } catch (e) {
      if (e instanceof Error && e.message !== `请求失败 (${response.status})`) throw e;
      throw new Error(`请求失败 (${response.status})`);
    }
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.msg || '登录失败');
  }

  return {
    token: data.data.token,
    user: data.data.user,
  };
}

/**
 * 验证 Token 有效性
 */
export async function checkAuth(token: string): Promise<AuthUser | null> {
  try {
    const response = await fetch(`${API_HOST}/auth/check`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    return data.code === 0 ? data.data.user : null;
  } catch {
    return null;
  }
}

/**
 * 修改密码
 */
export async function changePassword(token: string, oldPassword: string, newPassword: string): Promise<void> {
  const response = await fetch(`${API_HOST}/auth/change-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ oldPassword, newPassword }),
  });
  const data = await response.json();
  if (data.code !== 0) {
    throw new Error(data.msg || '修改密码失败');
  }
}

/**
 * 登出
 */
export async function logout(token: string): Promise<void> {
  try {
    await fetch(`${API_HOST}/auth/logout`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
  } catch {
    // 忽略错误
  }
}
